import {
  useGetFilesQuery,
  useGetFilesUrlsQuery,
  useUploadFileMutation,
  useUploadGoogleDriveFileMutation,
} from 'store/services/documentStorage/googleCloudStorage';
import { type ChangeEvent, memo, type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FileItem } from './FileItem';
import { FileItemSkeleton } from './FileItemSkeleton';
import { LoadFailedBlock } from './LoadFailedBlock';
import { Placeholder } from './Placeholder';

import Button from '@mui/material/Button';
import { Typography } from '@mui/material';
import type { FormItemUiDefinition } from 'types/formItem';
import { isMobileApp } from 'utils/common.ts';
import { DrivePicker, DrivePickerDocsView } from '@googleworkspace/drive-picker-react';
import type { PickerPickedEvent } from '@googleworkspace/drive-picker-element';
import { useBooleanState } from 'utils/hooks';
import { useRecordLockHeartbeat } from 'contexts';
import { normalizeUploadName } from 'utils/file/fileUtils';
import { RootWrapper, FilesWrapper, ButtonsWrapper, InputWrapper } from './FileUploadsItem.styles';

export interface FileListEventTarget extends EventTarget {
  files: File[];
}

export interface FileListEvent {
  target: FileListEventTarget & HTMLInputElement;
}

export const DEFAULT_MAX_FILE_SIZE_MB = 600;

interface Props {
  onChange?: () => void;
  formItemUiDefinition?: FormItemUiDefinition;

  [key: string]: any;
}

type FieldArr = { fileId: string };

const FileUploadsItemBase = ({ onChange, formItemUiDefinition, ...formItem }: Props) => {
  const { t } = useTranslation();
  const mobileApp = isMobileApp();
  const [isLoading, setIsLoading] = useState(false);
  const [googlePicker, toggleGooglePicker] = useBooleanState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const uploadInput = useRef<HTMLInputElement>(null);
  const recordLockHeartbeat = useRecordLockHeartbeat();
  const newlyAddedIds = useRef(new Set<string>());

  const { control, trigger } = useFormContext<{
    [x: string]: FieldArr[];
  }>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: formItem.id,
    rules: formItem.registerProps,
  });

  const {
    data: { files = [] } = {},
    isSuccess,
    isError: isFilesError,
    refetch: refetchFiles,
  } = useGetFilesQuery(
    {
      ids: fields.map(({ fileId }) => fileId),
    },
    { skip: fields.length === 0 }
  );

  const filesIds = useMemo(() => files.map(({ id }) => id), [files]);

  useEffect(() => {
    trigger(formItem.id);
  }, [fields.length, formItem.id, trigger]);

  const { data: { fileUrls: filesWithUrl = [] } = {} } = useGetFilesUrlsQuery(
    {
      ids: filesIds,
    },
    {
      skip: filesIds.length === 0,
    }
  );

  const filesData = useMemo(() => {
    if (!files.length || !filesWithUrl.length) {
      return [];
    }
    return files.map((file) => {
      const url = filesWithUrl.find((item) => item.id === file.id)?.url ?? '';
      return { ...file, url };
    });
  }, [files, filesWithUrl]);

  const maxFilesNumber = formItem.definition.fileUpload?.maxFilesNumber;
  const isDisable = formItem.detail || formItem.disabledUpload || (maxFilesNumber !== null && filesData.length >= maxFilesNumber);
  const isDisableRemove = formItem.detail || formItem.disabledRemove;

  const [fileUpload] = useUploadFileMutation();
  const [googleDriveFileUpload] = useUploadGoogleDriveFileMutation();

  const validateFiles = useCallback(
    <T,>(incomingFiles: T[]): T[] => {
      if (incomingFiles.length === 0) return [];

      if (maxFilesNumber !== null) {
        const remainingSlots = maxFilesNumber - filesData.length;

        if (remainingSlots <= 0) {
          setUploadError(t('user.detail.file.maxFilesReached', { max: maxFilesNumber }));
          return [];
        }

        if (incomingFiles.length > remainingSlots) {
          setUploadError(
            t('user.detail.file.onlySomeFilesUploaded', {
              uploaded: remainingSlots,
              total: incomingFiles.length,
              max: maxFilesNumber,
            })
          );

          return incomingFiles.slice(0, remainingSlots);
        } else {
          setUploadError(null);
        }
      }

      return incomingFiles;
    },
    [filesData.length, maxFilesNumber, t]
  );

  const handleUploadFile = useCallback(
    async (event: ChangeEvent<HTMLInputElement> | FileListEvent) => {
      const data = Array.from(event.target.files || []);

      if (data.length === 0) return;

      const filesToUpload = validateFiles(data);

      const oversizedFiles = filesToUpload.filter((file) => file.size > DEFAULT_MAX_FILE_SIZE_MB * 1024 * 1024);

      if (oversizedFiles.length > 0) {
        return setUploadError(`${t('common.maxSize')} ${DEFAULT_MAX_FILE_SIZE_MB}MB`);
      }

      try {
        setIsLoading(true);
        recordLockHeartbeat?.beginUploadingFiles();

        const uploadPromises = filesToUpload.map(async (file) => {
          const normalizedName = normalizeUploadName(file.name);
          const formData = new FormData();
          formData.append('file', file, normalizedName);

          const res = await fileUpload({
            data: formData,
            name: normalizedName,
          });

          if (res.data) {
            newlyAddedIds.current.add(res.data.id);
            append({ fileId: res.data.id });
          } else {
            setUploadError(t('user.detail.file.uploadFailed', { fileName: normalizedName }));
          }
        });

        await Promise.all(uploadPromises);
        onChange?.();
      } catch {
        return;
      } finally {
        recordLockHeartbeat?.endUploadingFiles();
        setIsLoading(false);
      }
    },
    [append, fileUpload, onChange, t, validateFiles, recordLockHeartbeat]
  );

  const handleUploadGoogleDriveFile = useCallback(
    async (e: PickerPickedEvent) => {
      const { docs } = e.detail;
      toggleGooglePicker();

      try {
        setIsLoading(true);
        recordLockHeartbeat?.beginUploadingFiles();
        const uploadPromises = docs.map(async (doc: { name: string; url: string; id: string }) => {
          const res = await googleDriveFileUpload({
            name: doc.name,
            embedUrl: doc.url,
            documentId: doc.id,
          });

          if (res.data) {
            newlyAddedIds.current.add(res.data.id);
            append({ fileId: res.data.id });
          }
        });

        await Promise.all(uploadPromises);
        onChange?.();
      } catch {
        return;
      } finally {
        recordLockHeartbeat?.endUploadingFiles();
        setIsLoading(false);
      }
    },
    [append, googleDriveFileUpload, onChange, toggleGooglePicker, recordLockHeartbeat]
  );

  const openUpload = useCallback(
    (e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      e.stopPropagation();
      if (isDisable) return;
      if (uploadInput.current) {
        uploadInput.current.value = '';
        uploadInput.current.click();
      }
    },
    [isDisable, uploadInput]
  );

  const onDrop = useCallback(
    (data: File[]) =>
      handleUploadFile({
        target: { files: data } as FileListEventTarget & HTMLInputElement,
      }),
    [handleUploadFile]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    noClick: true,
    disabled: isDisable,
  });

  useEffect(() => {
    if (!isSuccess || !files) return;

    const indexesToRemove = fields
      .map((field, index) => {
        if (newlyAddedIds.current.has(field.fileId)) {
          return null;
        }
        const fileExists = files.some((file) => file.id === field.fileId);
        return fileExists ? null : index;
      })
      .filter((index): index is number => index !== null)
      .sort((a, b) => b - a);

    indexesToRemove.forEach((index) => remove(index));
  }, [isSuccess, fields, remove, files]);

  const handleRemove = useCallback(
    (fileId: string) => () => {
      const fieldIndex = fields.findIndex((field) => field.fileId === fileId);
      if (fieldIndex !== -1) {
        remove(fieldIndex);
        trigger(formItem.id);
      }
    },
    [fields, formItem.id, remove, trigger]
  );

  const error = formItem.error ?? uploadError;

  return (
    <>
      <RootWrapper {...getRootProps()}>
        {isDisable ? null : (
          <ButtonsWrapper>
            <Button variant="outlined" onClick={openUpload}>
              + {t('common.upload')}
            </Button>
            {!mobileApp && (
              <Button variant="outlined" onClick={toggleGooglePicker}>
                + {t('common.uploadFromGoogle')}
              </Button>
            )}
          </ButtonsWrapper>
        )}
        <InputWrapper formItemUiDefinition={formItemUiDefinition} error={Boolean(error)} lightError={Boolean(formItem.lightError)}>
          <input
            {...getInputProps()}
            ref={uploadInput}
            disabled={isDisable}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleUploadFile}
          />
          <FilesWrapper>
            {filesData?.length === 0 ? (
              isFilesError && fields.length > 0 ? (
                <LoadFailedBlock message={t('user.detail.file.loadFailed')} retryLabel={t('user.detail.file.retry')} onRetry={refetchFiles} />
              ) : (
                <Placeholder isDisable={isDisable} onClick={openUpload} />
              )
            ) : (
              filesData.map((file) => (
                <FileItem disabledRemove={isDisableRemove} key={file.id} file={file} onChange={() => onChange?.()} onRemove={handleRemove(file.id)} />
              ))
            )}
            {isLoading && <FileItemSkeleton />}
          </FilesWrapper>
        </InputWrapper>
      </RootWrapper>
      {googlePicker && (
        <DrivePicker
          client-id={process.env.REACT_APP_GOOGLE_CLIENT_ID ?? ''}
          multiselect
          onPicked={handleUploadGoogleDriveFile}
          onCanceled={toggleGooglePicker}>
          <DrivePickerDocsView owned-by-me="true" />
        </DrivePicker>
      )}
      {error && (
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      )}
    </>
  );
};

export const FileUploadsItem = memo(FileUploadsItemBase) as typeof FileUploadsItemBase;
